<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import AddAttribute from './AddAttribute.vue';
import EditAttribute from './EditAttribute.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import AttributeListItem from 'dashboard/components-next/ConversationWorkflow/AttributeListItem.vue';
import AttributeAPI from 'dashboard/api/attributes';
import DeleteAttributeConfirmation from './DeleteAttributeConfirmation.vue';
import CategoriesPanel from './CategoriesPanel.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import Draggable from 'vuedraggable';
import { ATTRIBUTE_CATEGORIES } from './constants';

const { t } = useI18n();

const getters = useStoreGetters();
const store = useStore();
const { currentAccount } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const showAddPopup = ref(false);
const selectedTabIndex = ref(0);
const categoriesPanelRef = ref(null);
const addAttributeRef = ref(null);
const editAttributeRef = ref(null);
const uiFlags = computed(() => getters['attributes/getUIFlags'].value);
const showEditPopup = ref(false);
const [showDeletePopup, toggleDeletePopup] = useToggle(false);
const selectedAttribute = ref({});
const attributeUsage = ref({
  automation_rules: [],
  pricing_rules: [],
  pricing_items: [],
  procedures: [],
});

const openAddPopup = async () => {
  showAddPopup.value = true;
  await nextTick();
  addAttributeRef.value?.resetForm();
};
const openAddCategory = event => {
  categoriesPanelRef.value?.openCreateFlapout(event);
};
const dismissAddPopup = () => {
  showAddPopup.value = false;
};
const cancelAddPopup = () => {
  addAttributeRef.value?.resetForm();
  showAddPopup.value = false;
};
const handleAddModalClose = reason => {
  if (reason === 'cancel') cancelAddPopup();
  else dismissAddPopup();
};
const dismissEditPopup = () => {
  showEditPopup.value = false;
};
const cancelEditPopup = () => {
  editAttributeRef.value?.resetForm();
  showEditPopup.value = false;
};
const handleEditModalClose = reason => {
  if (reason === 'cancel') cancelEditPopup();
  else dismissEditPopup();
};
const closeDelete = () => {
  toggleDeletePopup(false);
  selectedAttribute.value = {};
  attributeUsage.value = {
    automation_rules: [],
    pricing_rules: [],
    pricing_items: [],
    procedures: [],
  };
};

const fetchAttributeUsage = async attributeId => {
  try {
    const response = await AttributeAPI.getUsage(attributeId);
    attributeUsage.value = response.data;
  } catch (error) {
    console.error('Error fetching attribute usage:', error);
    attributeUsage.value = {
      automation_rules: [],
      pricing_rules: [],
      pricing_items: [],
      procedures: [],
    };
  }
};

const tabs = computed(() => {
  return [
    {
      key: 0,
      name: t('ATTRIBUTES_MGMT.TABS.CONVERSATION'),
    },
    {
      key: 1,
      name: t('ATTRIBUTES_MGMT.TABS.CONTACT'),
    },
    {
      key: 2,
      name: t('ATTRIBUTES_MGMT.TABS.EVENT'),
    },
    {
      key: 3,
      name: t('ATTRIBUTES_MGMT.TABS.CATEGORIES'),
    },
  ];
});

const tabsForTabBar = computed(() =>
  tabs.value.map(tab => ({ label: tab.name, key: tab.key }))
);

onMounted(() => {
  store.dispatch('attributes/get');
  store.dispatch('customAttributeCategories/get');
});

const attributeModel = computed(() => {
  if (selectedTabIndex.value === 0) return 'conversation_attribute';
  if (selectedTabIndex.value === 1) return 'contact_attribute';
  return 'event_attribute';
});

const attributes = computed(() =>
  getters['attributes/getAttributesByModel'].value(attributeModel.value)
);

const onClickTabChange = tab => {
  selectedTabIndex.value = tab.key;
};

const handleEditAttribute = attribute => {
  selectedAttribute.value = attribute;
  showEditPopup.value = true;
};

const handleDeleteAttribute = async attribute => {
  selectedAttribute.value = attribute;
  await fetchAttributeUsage(attribute.id);
  toggleDeletePopup(true);
};

const confirmDeleteAttribute = async () => {
  try {
    await store.dispatch('attributes/delete', selectedAttribute.value.id);
    useAlert(t('ATTRIBUTES_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    closeDelete();
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('ATTRIBUTES_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const requiredAttributeKeys = computed(
  () => currentAccount.value?.settings?.conversation_required_attributes || []
);

const hasPreChatBadge = attribute => {
  return (inboxes.value || []).some(inbox => {
    const fields =
      inbox?.pre_chat_form_options?.pre_chat_fields ||
      inbox?.channel?.pre_chat_form_options?.pre_chat_fields ||
      [];
    return fields.some(field => field.name === attribute.attribute_key);
  });
};

const buildBadges = attribute => {
  const badges = [];
  if (hasPreChatBadge(attribute)) {
    badges.push({
      type: 'pre-chat',
    });
  }

  if (
    attribute.attribute_model === 'conversation_attribute' &&
    requiredAttributeKeys.value.includes(attribute.attribute_key)
  ) {
    badges.push({
      type: 'resolution',
    });
  }

  return badges;
};

const presentAttribute = attribute => ({
  ...attribute,
  label: attribute.attribute_display_name,
  type: attribute.attribute_display_type,
  value: attribute.attribute_key,
  badges: buildBadges(attribute),
});

const derivedAttributes = computed(() =>
  attributes.value.map(presentAttribute)
);

const PARENT_CATEGORY_LABELS = {
  INFO: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.INFO',
  AV: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.AV',
  CATERING: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.CATERING',
  SECURITY: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.SECURITY',
  TECHNICAL: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.TECHNICAL',
  CREW: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.CREW',
  OTHER: 'ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.OTHER',
};

const categoryRecords = computed(
  () => store.getters['customAttributeCategories/getRecords'] || []
);

const eventGroups = computed(() => {
  if (attributeModel.value !== 'event_attribute') return [];

  const byKey = {};
  attributes.value.forEach(attr => {
    const key = attr.custom_attribute_category_id
      ? `sub_${attr.custom_attribute_category_id}`
      : attr.attribute_category || 'info';
    if (!byKey[key]) byKey[key] = [];
    byKey[key].push(presentAttribute(attr));
  });
  Object.values(byKey).forEach(items => {
    items.sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id);
  });

  const groups = [];
  ATTRIBUTE_CATEGORIES.forEach(pc => {
    const parent = pc.key.toLowerCase();
    if (byKey[parent]?.length) {
      groups.push({
        key: parent,
        label: t(PARENT_CATEGORY_LABELS[pc.key]),
        items: byKey[parent],
      });
    }
    categoryRecords.value
      .filter(cat => cat.parent_category === parent)
      .sort(
        (a, b) =>
          a.position - b.position ||
          new Date(a.created_at) - new Date(b.created_at) ||
          a.id - b.id
      )
      .forEach(cat => {
        const key = `sub_${cat.id}`;
        if (!byKey[key]?.length) return;
        groups.push({ key, label: cat.name, items: byKey[key] });
      });
  });

  const used = new Set(groups.map(group => group.key));
  Object.keys(byKey).forEach(key => {
    if (used.has(key) || !byKey[key].length) return;
    groups.push({ key, label: key, items: byKey[key] });
  });
  return groups;
});

const localEventGroups = ref([]);

watch(
  eventGroups,
  groups => {
    localEventGroups.value = groups.map(group => ({
      ...group,
      items: [...group.items],
    }));
  },
  { immediate: true }
);

const onEventGroupDragEnd = async group => {
  if (!group.items || group.items.length < 2) return;
  const positionsHash = group.items.reduce((acc, item, index) => {
    acc[item.id] = index;
    return acc;
  }, {});
  try {
    await store.dispatch('attributes/reorder', positionsHash);
  } catch {
    useAlert(t('ATTRIBUTES_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('ATTRIBUTES_MGMT.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('ATTRIBUTES_MGMT.HEADER')"
        :description="$t('ATTRIBUTES_MGMT.DESCRIPTION')"
        :link-text="$t('ATTRIBUTES_MGMT.LEARN_MORE')"
        feature-name="custom_attributes"
      >
        <template #actions>
          <Button
            v-if="selectedTabIndex !== 3"
            icon="i-lucide-circle-plus"
            :label="$t('ATTRIBUTES_MGMT.HEADER_BTN_TXT')"
            md
            @click="openAddPopup"
          />
          <Button
            v-if="selectedTabIndex === 3"
            icon="i-lucide-circle-plus"
            :label="$t('ATTRIBUTES_MGMT.CATEGORIES.ADD_BUTTON')"
            md
            @click="openAddCategory"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <TabBar
          :tabs="tabsForTabBar"
          :initial-active-tab="selectedTabIndex"
          class="max-w-xl"
          @tab-changed="onClickTabChange"
        />
        <CategoriesPanel
          v-if="selectedTabIndex === 3"
          ref="categoriesPanelRef"
        />
        <template v-else>
          <div
            v-if="selectedTabIndex === 2 && localEventGroups.length"
            class="flex flex-col gap-6 pb-20"
          >
            <div
              v-for="group in localEventGroups"
              :key="group.key"
              class="flex flex-col gap-2"
            >
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ group.label }}
              </h3>
              <Draggable
                v-model="group.items"
                item-key="id"
                handle=".drag-handle"
                ghost-class="opacity-50"
                class="grid gap-3"
                @end="onEventGroupDragEnd(group)"
              >
                <template #item="{ element: attribute }">
                  <div class="flex items-start gap-2">
                    <span
                      class="drag-handle mt-4 shrink-0 flex cursor-grab text-n-slate-9 hover:text-n-slate-11"
                      role="button"
                      :aria-label="attribute.label"
                    >
                      <span class="i-lucide-grip-vertical size-4" />
                    </span>
                    <AttributeListItem
                      class="min-w-0 flex-1"
                      :attribute="attribute"
                      :badges="attribute.badges"
                      @edit="handleEditAttribute"
                      @delete="handleDeleteAttribute"
                    />
                  </div>
                </template>
              </Draggable>
            </div>
          </div>
          <div v-else-if="derivedAttributes.length" class="grid gap-3 pb-20">
            <AttributeListItem
              v-for="attribute in derivedAttributes"
              :key="attribute.id"
              :attribute="attribute"
              :badges="attribute.badges"
              @edit="handleEditAttribute"
              @delete="handleDeleteAttribute"
            />
          </div>
          <p
            v-else
            class="flex-1 py-20 text-n-slate-12 flex items-center justify-center text-base"
          >
            {{ $t('ATTRIBUTES_MGMT.LIST.EMPTY_RESULT.404') }}
          </p>
        </template>
      </div>
    </template>
    <woot-modal
      v-model:show="showAddPopup"
      :destroy-on-close="false"
      :on-close="handleAddModalClose"
    >
      <AddAttribute
        ref="addAttributeRef"
        :selected-attribute-model-tab="selectedTabIndex"
        @close="cancelAddPopup"
      />
    </woot-modal>
    <woot-modal
      v-model:show="showEditPopup"
      :destroy-on-close="false"
      :on-close="handleEditModalClose"
    >
      <EditAttribute
        v-if="selectedAttribute.id"
        ref="editAttributeRef"
        :selected-attribute="selectedAttribute"
        :is-updating="uiFlags.isUpdating"
        @on-close="cancelEditPopup"
      />
    </woot-modal>
    <DeleteAttributeConfirmation
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :attribute="selectedAttribute"
      :usage="attributeUsage"
      @confirm="confirmDeleteAttribute"
      @close="closeDelete"
    />
  </SettingsLayout>
</template>
